import {
  Button,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  mergeClasses,
  Popover,
  PopoverSurface,
  PopoverTrigger,
  Skeleton,
  SkeletonItem,
} from "@fluentui/react-components";
import {
  AddFilled,
  ArrowUpRightRegular,
  Checkmark12Regular,
  ChevronDown12Regular,
  EyeRegular,
  GaugeRegular,
  GlobeRegular,
  LinkRegular,
  SearchRegular,
} from "@fluentui/react-icons";
import { useDeleteDiagnostic } from "apps/project-iq/hooks/useDeleteDiagnostic";
import { useLinks } from "apps/project-iq/hooks/useLinks";
import { useWorkspaceDiagnostics } from "apps/project-iq/hooks/useWorkspaceDiagnostics";
import { IDiagnostic } from "apps/project-iq/models/IDiagnostic";
import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { CreateDiagnosticDialog } from "../CreateDiagnosticDialog/CreateDiagnosticDialog";
import { DiagnosticCard } from "./DiagnosticCard";
import { useStyles } from "./WorkspaceDiagnostics.styles";
import {
  DiagnosticSortBy,
  IWorkspaceDiagnosticsProps,
} from "./WorkspaceDiagnostics.types";

const SORT_OPTIONS: { value: DiagnosticSortBy; label: string }[] = [
  { value: "last_updated", label: "Last updated" },
  { value: "name", label: "Name" },
  { value: "date_created", label: "Date created" },
];

const SKELETON_CARD_COUNT = 4;
const QUICK_LINK_SKELETON_COUNT = 3;

const DiagnosticCardSkeleton = (): JSX.Element => {
  const styles = useStyles();
  return (
    <div className={styles.skeletonCard}>
      <Skeleton>
        <div className={styles.skeletonCardInner}>
          <SkeletonItem animation="wave" className={styles.skeletonCardIcon} />
          <div className={styles.skeletonCardBody}>
            <div className={styles.skeletonCardNameRow}>
              <SkeletonItem
                animation="wave"
                className={styles.skeletonCardName}
              />
              <SkeletonItem
                animation="wave"
                className={styles.skeletonCardPill}
              />
            </div>
            <SkeletonItem
              animation="wave"
              className={styles.skeletonCardDescription}
            />
            <SkeletonItem
              animation="wave"
              className={styles.skeletonCardMeta}
            />
          </div>
          <SkeletonItem animation="wave" className={styles.skeletonCardArrow} />
        </div>
      </Skeleton>
    </div>
  );
};

const QuickLinkCardSkeleton = (): JSX.Element => {
  const styles = useStyles();
  return (
    <Skeleton>
      <div className={styles.quickLinkCard} style={{ pointerEvents: "none" }}>
        <SkeletonItem
          animation="wave"
          className={styles.quickLinkSkeletonIcon}
        />
        <div className={styles.quickLinkContent}>
          <SkeletonItem
            animation="wave"
            className={styles.quickLinkSkeletonTitle}
          />
          <SkeletonItem
            animation="wave"
            className={styles.quickLinkSkeletonSubtitle}
          />
        </div>
      </div>
    </Skeleton>
  );
};

export const WorkspaceDiagnostics = (
  props: IWorkspaceDiagnosticsProps,
): JSX.Element => {
  const { workspaceId } = props;
  const styles = useStyles();
  const navigate = useNavigate();

  const [searchQuery, setSearchQuery] = useState<string>("");
  const [sortBy, setSortBy] = useState<DiagnosticSortBy>("last_updated");
  const [isSortMenuOpen, setIsSortMenuOpen] = useState<boolean>(false);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState<boolean>(false);
  const [editingDiagnostic, setEditingDiagnostic] =
    useState<IDiagnostic | null>(null);
  const [deletingDiagnostic, setDeletingDiagnostic] =
    useState<IDiagnostic | null>(null);

  const currentSortLabel =
    SORT_OPTIONS.find((option) => option.value === sortBy)?.label ??
    "Last updated";

  const {
    mutateAsync: deleteDiagnosticMutation,
    isLoading: isDeletingDiagnostic,
  } = useDeleteDiagnostic(workspaceId);

  const handleConfirmDelete = async (): Promise<void> => {
    if (!deletingDiagnostic) return;
    await deleteDiagnosticMutation({ id: deletingDiagnostic.id });
    setDeletingDiagnostic(null);
  };

  const {
    data: diagnostics,
    isLoading,
    isFetching,
  } = useWorkspaceDiagnostics(workspaceId);
  const isLoadingDiagnostics = isLoading || isFetching;
  const { links: quickLinks, isLoading: isQuickLinksLoading } = useLinks(
    "nh_diagnostic_iq_quick_links",
  );

  const filteredDiagnostics = useMemo(() => {
    const list: IDiagnostic[] = diagnostics ?? [];

    const filtered = searchQuery.trim()
      ? list.filter((d) => {
          const query = searchQuery.toLowerCase();
          return (
            d.name.toLowerCase().includes(query) ||
            d.description.toLowerCase().includes(query)
          );
        })
      : list;

    return [...filtered].sort((a, b) => {
      if (sortBy === "name") return a.name.localeCompare(b.name);
      if (sortBy === "date_created") return b.createdAt - a.createdAt;
      return b.updatedAt - a.updatedAt;
    });
  }, [diagnostics, searchQuery, sortBy]);

  return (
    <div className={styles.root}>
      <div className={styles.body}>
        <div className={styles.content}>
          <div className={styles.hero}>
            <div className={styles.heroEyebrow} aria-hidden="true">
              <GaugeRegular className={styles.heroEyebrowIcon} />
              <span>NH Diagnostic IQ</span>
            </div>
            <h2 className={styles.heroTitle}>Run a diagnostic</h2>
            <p className={styles.heroDescription}>
              Browse the diagnostics already running on this engagement, or
              start a new one.
            </p>
          </div>

          <section className={styles.section}>
            <div className={styles.sectionHeader}>
              <div className={styles.sectionBadge} aria-hidden="true">
                <GlobeRegular className={styles.sectionBadgeIcon} />
              </div>
              <h3 className={styles.sectionTitle}>Quick links</h3>
            </div>
            <div className={styles.quickLinksGrid}>
              {isQuickLinksLoading
                ? Array.from({ length: QUICK_LINK_SKELETON_COUNT }).map(
                    (_, i) => <QuickLinkCardSkeleton key={i} />,
                  )
                : quickLinks.map((link) => (
                    <a
                      key={link.id}
                      href={link.url}
                      target="_blank"
                      rel="noreferrer"
                      aria-label={`${link.displayName} (opens in new tab)`}
                      className={styles.quickLinkCard}
                    >
                      <div
                        className={styles.quickLinkIconContainer}
                        aria-hidden="true"
                      >
                        <LinkRegular />
                      </div>
                      <div className={styles.quickLinkContent}>
                        <div className={styles.quickLinkTitle}>
                          {link.displayName}
                        </div>
                        {link.subtext && (
                          <div className={styles.quickLinkSubtitle}>
                            {link.subtext}
                          </div>
                        )}
                      </div>
                      <div className={styles.quickLinkArrow} aria-hidden="true">
                        <ArrowUpRightRegular />
                      </div>
                    </a>
                  ))}
            </div>
          </section>

          <section className={styles.section}>
            <div className={styles.sectionHeader}>
              <div className={styles.sectionBadge} aria-hidden="true">
                <EyeRegular className={styles.sectionBadgeIcon} />
              </div>
              <div className={styles.sectionTitleGroup}>
                <h3 className={styles.sectionTitle}>Diagnostics</h3>
                <p className={styles.sectionDescription}>
                  Every diagnostic run on this engagement.
                </p>
              </div>
              <div className={styles.sectionAction}>
                <Button
                  appearance="primary"
                  icon={<AddFilled />}
                  className={styles.newDiagnosticButton}
                  onClick={() => setIsCreateDialogOpen(true)}
                >
                  New Diagnostic
                </Button>
              </div>
            </div>

            <div className={styles.diagnosticsControls}>
              <div className={styles.searchWrapper}>
                <span className={styles.searchIcon} aria-hidden="true">
                  <SearchRegular />
                </span>
                <input
                  type="search"
                  className={styles.searchNativeInput}
                  placeholder="Search diagnostics by name or description"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  aria-label="Search diagnostics"
                />
              </div>
              <div className={styles.sortWrapper}>
                <Popover
                  open={isSortMenuOpen}
                  onOpenChange={(_, data) => setIsSortMenuOpen(data.open)}
                  positioning="below-end"
                >
                  <PopoverTrigger disableButtonEnhancement>
                    <button
                      className={styles.sortTrigger}
                      aria-label={`Sort by: ${currentSortLabel}`}
                      aria-haspopup="listbox"
                      aria-expanded={isSortMenuOpen}
                    >
                      <span
                        className={styles.sortTriggerText}
                        aria-hidden="true"
                      >
                        Sort by · {currentSortLabel}
                      </span>
                      <ChevronDown12Regular
                        className={mergeClasses(
                          styles.sortTriggerChevron,
                          isSortMenuOpen && styles.sortTriggerChevronOpen,
                        )}
                        aria-hidden="true"
                      />
                    </button>
                  </PopoverTrigger>
                  <PopoverSurface
                    className={styles.sortPopoverSurface}
                    role="listbox"
                  >
                    {SORT_OPTIONS.map((option) => {
                      const isActive = option.value === sortBy;
                      return (
                        <button
                          key={option.value}
                          className={mergeClasses(
                            styles.sortMenuItem,
                            isActive && styles.sortMenuItemActive,
                          )}
                          onClick={() => {
                            setSortBy(option.value);
                            setIsSortMenuOpen(false);
                          }}
                          role="option"
                          aria-selected={isActive}
                        >
                          <span className={styles.sortMenuItemLabel}>
                            {option.label}
                          </span>
                          {isActive && (
                            <Checkmark12Regular aria-hidden="true" />
                          )}
                        </button>
                      );
                    })}
                  </PopoverSurface>
                </Popover>
              </div>
            </div>

            {isLoadingDiagnostics ? (
              <div className={styles.diagnosticsList}>
                {Array.from({ length: SKELETON_CARD_COUNT }).map((_, i) => (
                  <DiagnosticCardSkeleton key={i} />
                ))}
              </div>
            ) : filteredDiagnostics.length === 0 ? (
              <div className={styles.emptyState}>
                {searchQuery.trim()
                  ? "No diagnostics match your search."
                  : "No diagnostics yet. Create your first diagnostic to get started."}
              </div>
            ) : (
              <div className={styles.diagnosticsList}>
                {filteredDiagnostics.map((diagnostic) => (
                  <DiagnosticCard
                    key={diagnostic.id}
                    diagnostic={diagnostic}
                    onNavigate={() =>
                      navigate(
                        `/workspace/${workspaceId}/nh-diagnostic-iq/${diagnostic.id}`,
                        { state: { diagnostic } },
                      )
                    }
                    onEdit={() => setEditingDiagnostic(diagnostic)}
                    onDelete={() => setDeletingDiagnostic(diagnostic)}
                  />
                ))}
              </div>
            )}
          </section>
        </div>
      </div>
      <CreateDiagnosticDialog
        isOpen={isCreateDialogOpen}
        workspaceId={workspaceId}
        onDismiss={() => setIsCreateDialogOpen(false)}
      />
      {editingDiagnostic && (
        <CreateDiagnosticDialog
          isOpen={true}
          workspaceId={workspaceId}
          mode="edit"
          diagnostic={editingDiagnostic}
          onDismiss={() => setEditingDiagnostic(null)}
        />
      )}
      <Dialog
        open={deletingDiagnostic !== null}
        modalType="alert"
        onOpenChange={(_, data) => {
          if (!data.open) setDeletingDiagnostic(null);
        }}
      >
        <DialogSurface>
          <DialogBody>
            <DialogTitle>
              Delete &ldquo;{deletingDiagnostic?.name}&rdquo;?
            </DialogTitle>
            <DialogContent>
              This will permanently delete the diagnostic and cannot be undone.
            </DialogContent>
            <DialogActions>
              <Button
                appearance="secondary"
                onClick={() => setDeletingDiagnostic(null)}
                disabled={isDeletingDiagnostic}
              >
                Cancel
              </Button>
              <Button
                appearance="primary"
                onClick={handleConfirmDelete}
                disabled={isDeletingDiagnostic}
              >
                Delete
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>
    </div>
  );
};
